import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Loader2, Sparkles, Download, Play, Pause, RotateCcw,
  Volume2, Type, Maximize2, AlertTriangle, CheckCircle2, Film, Wand2,
} from "lucide-react";

const STATUS_STEPS = [
  { key: "uploaded",           label: "Uploaded" },
  { key: "transcribing",       label: "Transcribing" },
  { key: "transcribed",        label: "Transcript ready" },
  { key: "finding_highlights", label: "Finding highlights" },
  { key: "ready_to_edit",      label: "Ready to edit" },
];

const EDIT_PRESETS = [
  { v: "bold_viral",          label: "Bold Viral",          desc: "Fast cuts · big captions · punchy" },
  { v: "clean_professional",  label: "Clean Professional",  desc: "Smooth · minimal · polished" },
  { v: "podcast_clips",       label: "Podcast Clips",       desc: "Dynamic · speaker labels" },
  { v: "cinematic_story",     label: "Cinematic Story",     desc: "Slower · emotional pacing" },
  { v: "educational",         label: "Educational",         desc: "Structured · key-point callouts" },
  { v: "high_energy_promo",   label: "High-Energy Promo",   desc: "Fast · CTA-focused" },
];

const CAPTION_PRESETS = [
  { v: "pop",           label: "Pop",           desc: "Large centered · animated" },
  { v: "minimal",       label: "Minimal",       desc: "Clean bottom subtitles" },
  { v: "karaoke",       label: "Karaoke",       desc: "Word-by-word highlight" },
  { v: "bold_headline", label: "Bold Headline", desc: "Top banner + subtitles" },
  { v: "creator",       label: "Creator",       desc: "Energetic dynamic" },
  { v: "lower_third",   label: "Lower Third",   desc: "Professional lower" },
];

const ASPECTS = [
  { v: "9:16", label: "9:16 · Vertical" },
  { v: "1:1",  label: "1:1 · Square" },
  { v: "4:5",  label: "4:5 · Portrait" },
  { v: "16:9", label: "16:9 · Horizontal" },
];

function scorePillColor(s) {
  if (s >= 80) return "text-emerald-400 border-emerald-500/40 bg-emerald-500/10";
  if (s >= 60) return "text-yellow-400 border-yellow-500/40 bg-yellow-500/10";
  return "text-muted-foreground border-border";
}

export default function StudioProject() {
  const { projectId } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [highlights, setHighlights] = useState(null);
  const [renders, setRenders] = useState([]);
  const [selectedClip, setSelectedClip] = useState(0);
  const [mutedIds, setMutedIds] = useState(new Set());
  const [editingStyle, setEditingStyle] = useState("bold_viral");
  const [captionStyle, setCaptionStyle] = useState("pop");
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [aspect, setAspect] = useState("9:16");
  const [resolution, setResolution] = useState("720p");
  const [musicBed, setMusicBed] = useState("off");
  const [musicVolume, setMusicVolume] = useState(0.25);
  const [rendering, setRendering] = useState(false);
  const [activeRenderId, setActiveRenderId] = useState(null);
  const [revisionPrompt, setRevisionPrompt] = useState("");
  const [revising, setRevising] = useState(false);
  const [lastRevision, setLastRevision] = useState(null);
  const pollRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const p = await api.get(`/api/video/projects/${projectId}`);
      setProject(p.data);
      if (p.data.has_transcript) {
        const t = await api.get(`/api/video/projects/${projectId}/transcript`);
        setTranscript(t.data);
      }
      if (p.data.has_highlights) {
        const h = await api.get(`/api/video/projects/${projectId}/highlights`);
        setHighlights(h.data);
      }
      const r = await api.get(`/api/video/projects/${projectId}/renders`);
      setRenders(r.data);
      return p.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load project");
      return null;
    }
  }, [projectId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll while project is still processing OR a render is in progress
  useEffect(() => {
    const stillProcessing = project && ["uploaded","transcribing","transcribed","finding_highlights"].includes(project.status);
    const renderRunning = renders.some((r) => r.status === "queued" || r.status === "rendering");
    if (stillProcessing || renderRunning) {
      pollRef.current = setInterval(loadAll, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [project?.status, renders, loadAll]);

  const toggleSegment = (id) => {
    setMutedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const startRender = async () => {
    setRendering(true);
    try {
      const r = await api.post(`/api/video/projects/${projectId}/render`, {
        clip_index: selectedClip,
        editing_style: editingStyle,
        caption_style: captionStyle,
        captions_enabled: captionsEnabled,
        aspect,
        resolution,
        muted_segment_ids: Array.from(mutedIds),
        music_bed: musicBed,
        music_volume: musicVolume,
      });
      setActiveRenderId(r.data.render_id);
      toast.success("Render started. This takes 30–90 seconds.");
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Render failed to start");
    } finally {
      setRendering(false);
    }
  };

  const applyRevision = async () => {
    if (!revisionPrompt.trim()) return;
    setRevising(true);
    setLastRevision(null);
    try {
      const r = await api.post(`/api/video/projects/${projectId}/revise`, {
        prompt: revisionPrompt.trim(),
        clip_index: selectedClip,
        current_settings: {
          editing_style: editingStyle,
          caption_style: captionStyle,
          captions_enabled: captionsEnabled,
          aspect,
          resolution,
          music_bed: musicBed,
          music_volume: musicVolume,
        },
      });
      const changes = r.data.changes || {};
      if (changes.editing_style) setEditingStyle(changes.editing_style);
      if (changes.caption_style) setCaptionStyle(changes.caption_style);
      if (typeof changes.captions_enabled === "boolean") setCaptionsEnabled(changes.captions_enabled);
      if (changes.aspect) setAspect(changes.aspect);
      if (changes.resolution) setResolution(changes.resolution);
      if (changes.music_bed) setMusicBed(changes.music_bed);
      if (typeof changes.music_volume === "number") setMusicVolume(Math.max(0, Math.min(1, changes.music_volume)));
      if (Array.isArray(changes.mute_segment_ids) && changes.mute_segment_ids.length > 0) {
        setMutedIds((prev) => {
          const next = new Set(prev);
          changes.mute_segment_ids.forEach((id) => next.add(id));
          return next;
        });
      }
      if (Array.isArray(changes.restore_segment_ids) && changes.restore_segment_ids.length > 0) {
        setMutedIds((prev) => {
          const next = new Set(prev);
          changes.restore_segment_ids.forEach((id) => next.delete(id));
          return next;
        });
      }
      setLastRevision(changes);
      setRevisionPrompt("");
      toast.success("Settings updated — review and hit Render");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revision failed");
    } finally {
      setRevising(false);
    }
  };

  if (!project) {
    return <div className="p-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2"/>Loading project…</div>;
  }

  const stepIdx = STATUS_STEPS.findIndex((s) => s.key === project.status);
  const currentClip = highlights?.clips?.[selectedClip];
  const clipSegments = transcript && currentClip
    ? transcript.segments.filter((s) => s.end > currentClip.start && s.start < currentClip.end)
    : [];
  const latestRender = renders[0];

  return (
    <div className="p-8 max-w-6xl mx-auto pb-24">
      <button onClick={()=>nav("/app/studio")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-6" data-testid="back-to-studio">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Studio
      </button>

      <header className="mb-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-2">
          <Film className="h-3.5 w-3.5" /> {project.target_platform} · {project.content_goal.replace(/_/g,' ')}
        </div>
        <h1 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tighter" data-testid="project-title">{project.title}</h1>
        {project.duration_sec && (
          <div className="text-sm text-muted-foreground mt-1">Source: {(project.duration_sec/60).toFixed(1)} min</div>
        )}
      </header>

      {project.status === "error" && (
        <div className="rounded border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3 mb-6">
          <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5"/>
          <div>
            <div className="font-semibold text-red-400">Processing error</div>
            <div className="text-sm text-muted-foreground mt-1">{project.error || "Something went wrong. Try creating a new project."}</div>
          </div>
        </div>
      )}

      {/* Processing progress */}
      {!highlights && project.status !== "error" && (
        <div className="rounded-lg border border-border bg-card p-6 mb-8" data-testid="processing-panel">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Processing</div>
          <h3 className="font-display font-bold text-xl mt-1">Analyzing your video</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Transcribing audio, then identifying the strongest 20–60 second clips. This usually takes 30 seconds to a few minutes.
          </p>
          <ol className="mt-5 space-y-3">
            {STATUS_STEPS.map((s, i) => {
              const done = i < stepIdx || project.has_highlights;
              const active = i === stepIdx && !project.has_highlights;
              return (
                <li key={s.key} className="flex items-center gap-3">
                  <div className={`h-6 w-6 rounded-full grid place-items-center text-xs font-bold
                    ${done ? "bg-emerald-500 text-black" : active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                    {done ? <CheckCircle2 className="h-3.5 w-3.5"/> : active ? <Loader2 className="h-3 w-3 animate-spin"/> : (i+1)}
                  </div>
                  <span className={done ? "text-foreground" : active ? "text-foreground font-semibold" : "text-muted-foreground"}>
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Highlights & editor */}
      {highlights && highlights.clips.length > 0 && (
        <>
          <section className="mb-8">
            <h2 className="font-display font-bold text-xl tracking-tight mb-1">Recommended clips</h2>
            <p className="text-sm text-muted-foreground mb-4">Pick which clip to edit. Each has a Viral Potential Score based on hook strength, clarity, and emotional impact.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {highlights.clips.map((c, i) => (
                <button
                  key={c.id}
                  onClick={()=>{ setSelectedClip(i); setMutedIds(new Set()); }}
                  className={`text-left rounded-lg border-2 p-4 transition ${selectedClip===i ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/30"}`}
                  data-testid={`clip-card-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Clip {i+1}</div>
                    <div className={`text-xs font-mono px-2 py-0.5 rounded-full border ${scorePillColor(c.viral_score)}`}>
                      {c.viral_score} / 100
                    </div>
                  </div>
                  <div className="mt-2 font-semibold line-clamp-3 text-sm">{c.hook}</div>
                  <div className="mt-3 text-[11px] text-muted-foreground">
                    <span className="font-mono">{c.start.toFixed(1)}s → {c.end.toFixed(1)}s</span>
                    <span className="ml-2">· {c.duration}s</span>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground line-clamp-3">{c.rationale}</div>
                </button>
              ))}
            </div>
          </section>

          {currentClip && (
            <>
              <section className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8">
                <div className="lg:col-span-3">
                  <h3 className="font-display font-bold text-lg tracking-tight mb-2">Transcript editor</h3>
                  <p className="text-xs text-muted-foreground mb-3">Click a sentence to remove it from the clip. Removed sentences are cut out during render.</p>
                  <div className="rounded border border-border bg-card p-4 max-h-80 overflow-y-auto space-y-2" data-testid="transcript-editor">
                    {clipSegments.map((s) => {
                      const muted = mutedIds.has(s.id);
                      return (
                        <button
                          key={s.id}
                          onClick={()=>toggleSegment(s.id)}
                          className={`block w-full text-left px-3 py-2 rounded text-sm transition ${
                            muted ? "opacity-40 line-through bg-secondary" : "hover:bg-secondary/50"
                          }`}
                          data-testid={`segment-${s.id.slice(0,8)}`}
                        >
                          <span className="font-mono text-[10px] text-muted-foreground mr-2">{s.start.toFixed(1)}s</span>
                          {s.text}
                        </button>
                      );
                    })}
                  </div>
                  {mutedIds.size > 0 && (
                    <button
                      className="mt-2 text-xs text-primary hover:underline flex items-center gap-1"
                      onClick={()=>setMutedIds(new Set())}
                      data-testid="restore-all"
                    >
                      <RotateCcw className="h-3 w-3"/> Restore {mutedIds.size} removed sentence{mutedIds.size>1?"s":""}
                    </button>
                  )}
                </div>

                <aside className="lg:col-span-2 space-y-5">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Editing style</div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {EDIT_PRESETS.map(p => (
                        <button
                          key={p.v}
                          onClick={()=>setEditingStyle(p.v)}
                          className={`text-left px-3 py-2 rounded border text-xs transition ${editingStyle===p.v ? "border-primary bg-primary/10" : "border-border hover:border-primary/30"}`}
                          data-testid={`edit-style-${p.v}`}
                        >
                          <div className="font-semibold">{p.label}</div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">{p.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2 flex items-center gap-2">
                      <Type className="h-3 w-3"/> Captions
                      <label className="ml-auto inline-flex items-center gap-1.5 cursor-pointer normal-case tracking-normal font-normal">
                        <input type="checkbox" checked={captionsEnabled} onChange={(e)=>setCaptionsEnabled(e.target.checked)} data-testid="captions-toggle"/>
                        <span className="text-[10px]">{captionsEnabled ? "On" : "Off"}</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5" style={{opacity: captionsEnabled ? 1 : 0.4, pointerEvents: captionsEnabled ? "auto" : "none"}}>
                      {CAPTION_PRESETS.map(p => (
                        <button
                          key={p.v}
                          onClick={()=>setCaptionStyle(p.v)}
                          className={`text-left px-3 py-2 rounded border text-xs transition ${captionStyle===p.v ? "border-primary bg-primary/10" : "border-border hover:border-primary/30"}`}
                          data-testid={`caption-style-${p.v}`}
                        >
                          <div className="font-semibold">{p.label}</div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">{p.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2 flex items-center gap-2">
                      <Volume2 className="h-3 w-3"/> Music bed
                    </div>
                    <div className="grid grid-cols-5 gap-1">
                      {[
                        {v:"off",label:"Off"},
                        {v:"energetic",label:"Energetic"},
                        {v:"cinematic",label:"Cinema"},
                        {v:"chill",label:"Chill"},
                        {v:"upbeat",label:"Upbeat"},
                      ].map(m => (
                        <button
                          key={m.v}
                          onClick={()=>setMusicBed(m.v)}
                          className={`text-center px-2 py-1.5 rounded border text-[10px] font-semibold uppercase tracking-wide transition ${musicBed===m.v ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/30"}`}
                          data-testid={`music-${m.v}`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                    {musicBed !== "off" && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                          <span>Music volume</span>
                          <span className="font-mono">{Math.round(musicVolume*100)}%</span>
                        </div>
                        <input
                          type="range" min={0} max={0.6} step={0.05}
                          value={musicVolume}
                          onChange={(e)=>setMusicVolume(parseFloat(e.target.value))}
                          className="w-full accent-primary"
                          data-testid="music-volume"
                        />
                        <div className="text-[10px] text-muted-foreground mt-1">Ducks under speech automatically.</div>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2 flex items-center gap-2">
                      <Maximize2 className="h-3 w-3"/> Export
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      <select value={aspect} onChange={(e)=>setAspect(e.target.value)}
                        className="rounded border border-border bg-background px-2 py-2 text-xs" data-testid="aspect-select">
                        {ASPECTS.map(a => <option key={a.v} value={a.v}>{a.label}</option>)}
                      </select>
                      <select value={resolution} onChange={(e)=>setResolution(e.target.value)}
                        className="rounded border border-border bg-background px-2 py-2 text-xs" data-testid="resolution-select">
                        <option value="720p">720p</option>
                        <option value="1080p">1080p</option>
                      </select>
                    </div>
                  </div>

                  <Button className="w-full" size="lg" onClick={startRender} disabled={rendering} data-testid="render-btn">
                    <Sparkles className="h-4 w-4"/> {rendering ? "Starting…" : "Render clip"}
                  </Button>
                </aside>
              </section>

              {/* AI Revision Prompt */}
              <section className="mb-8 rounded-lg border border-border bg-card p-5" data-testid="revision-panel">
                <div className="flex items-center gap-2 mb-1">
                  <Wand2 className="h-4 w-4 text-primary"/>
                  <h3 className="font-display font-bold text-lg tracking-tight">What would you like to change?</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Describe the tweak in plain English. Try: "make it more professional", "add music", "make a LinkedIn version", "remove the pricing section".
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <textarea
                    rows={2}
                    value={revisionPrompt}
                    onChange={(e)=>setRevisionPrompt(e.target.value)}
                    onKeyDown={(e)=>{
                      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") applyRevision();
                    }}
                    className="flex-1 rounded border border-border bg-background px-3 py-2 text-sm resize-none"
                    placeholder="Make the first 3 seconds more exciting…"
                    disabled={revising}
                    data-testid="revision-prompt"
                  />
                  <Button onClick={applyRevision} disabled={revising || !revisionPrompt.trim()} data-testid="revision-apply">
                    {revising ? <Loader2 className="h-4 w-4 animate-spin"/> : <Wand2 className="h-4 w-4"/>}
                    {revising ? "Thinking…" : "Apply"}
                  </Button>
                </div>
                {lastRevision && (
                  <div className="mt-3 rounded bg-primary/5 border border-primary/20 px-3 py-2 text-xs">
                    <span className="font-semibold text-primary">Applied. </span>
                    <span className="text-muted-foreground">{lastRevision.explanation}</span>
                  </div>
                )}
              </section>

              <ClipInsight clip={currentClip} />
              <RenderList renders={renders} apiBase={API}/>
            </>
          )}
        </>
      )}
    </div>
  );
}

function ClipInsight({ clip }) {
  const b = clip.viral_breakdown || {};
  return (
    <section className="mb-8">
      <h3 className="font-display font-bold text-lg tracking-tight mb-3 flex items-center gap-2">
        <Wand2 className="h-4 w-4"/> Viral Potential — {clip.viral_score} / 100
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {Object.entries(b).map(([k,v]) => (
          <div key={k} className="rounded border border-border bg-card px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{k.replace(/_/g,' ')}</div>
            <div className="font-mono text-lg font-bold mt-0.5">{v}</div>
          </div>
        ))}
      </div>
      {clip.improvements?.length > 0 && (
        <div className="mt-4 rounded border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Ways to improve</div>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            {clip.improvements.slice(0,3).map((imp,i)=><li key={`imp-${i}-${(imp||"").slice(0,20)}`}>{imp}</li>)}
          </ul>
        </div>
      )}
    </section>
  );
}

function RenderList({ renders, apiBase }) {
  if (renders.length === 0) return null;
  return (
    <section>
      <h3 className="font-display font-bold text-lg tracking-tight mb-3">Renders</h3>
      <div className="space-y-2" data-testid="render-list">
        {renders.map(r => (
          <div key={r.id} className="rounded border border-border bg-card px-4 py-3 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">
                Clip {r.clip_index+1} · {r.aspect} · {r.resolution} · {r.editing_style.replace(/_/g,' ')}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">{r.created_at?.slice(0,16).replace('T',' ')}</div>
            </div>
            {r.status === "done" ? (
              <>
                <video
                  src={`${apiBase}/video/renders/${r.id}/download?auth=${localStorage.getItem('contentos_token')}`}
                  className="h-16 rounded bg-black"
                  controls
                  playsInline
                />
                <a href={`${apiBase}/video/renders/${r.id}/download?auth=${localStorage.getItem('contentos_token')}`}
                   target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded bg-primary text-primary-foreground hover:opacity-90"
                   data-testid={`download-${r.id}`}
                >
                  <Download className="h-3.5 w-3.5"/> Download
                </a>
              </>
            ) : r.status === "error" ? (
              <div className="text-xs text-red-400 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5"/> {r.error?.slice(0,80) || "Error"}</div>
            ) : (
              <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin"/> {r.status}</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
